import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import clsx from "clsx";

import SectionHeader from "../common/SectionHeader";
import RevealOnScroll from "@/components/common/RevealOnScroll";

// ----------------------------------------------------------------------------

export type ITeamSpirit = {
  heading: string;
  description: string;
  position: "left_column" | "right_column";
};

export type ITeamSpiritSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  description?: ReactNode;
  image?: { src: string; alt: string };
  teamSpirits?: ITeamSpirit[];
};

// ---------------------------------------------------------------------------------

const TeamSpiritSection: FC<ITeamSpiritSectionProps> = (props) => {
  const { heading, description, teamSpirits, image, className, ...other } =
    props;

  // Split teamSpirits into left and right columns
  const leftItems = teamSpirits?.filter(
    (item) => item.position === "left_column",
  );

  const rightItems = teamSpirits?.filter(
    (item) => item.position === "right_column",
  );

  return (
    <section
      className={clsx("container py-14 md:py-20 xl:py-24", className)}
      {...other}
    >
      {(heading || description) && (
        <RevealOnScroll>
          <SectionHeader title={heading} subtitle={description} align="left" />
        </RevealOnScroll>
      )}

      {/* Render team spirits in two columns */}

      <div className="mt-7 grid grid-cols-1 gap-y-6 lg:mt-12 lg:grid-cols-3">
        {/* Left column */}
        {leftItems && leftItems.length > 0 && (
          <div className="flex w-full flex-col gap-y-6 lg:w-full lg:gap-y-12">
            {leftItems.map((spirit, index) => (
              <div
                key={index}
                className="flex flex-col items-start lg:items-end"
              >
                {/* Render title and description */}
                <div className="mb-3 min-w-full rounded-2xl border-t-2 border-secondary-900 bg-primary-100 p-3 lg:mb-5 lg:min-w-[275px] lg:px-4 lg:py-5">
                  <span className="block text-center text-lg font-normal text-black-950 lg:text-h6 xl:text-h5">
                    {spirit.heading}
                  </span>
                </div>
                <p className="text-wrap break-words text-base font-normal text-gray-500 lg:text-end">
                  {spirit.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Middle image column */}
        {image && (
          <div className="flex h-full w-full flex-col items-center justify-center lg:px-6">
            <Image
              src={image.src}
              alt={image.alt}
              height={514}
              width={307}
              priority
            />
          </div>
        )}

        {/* Right column */}
        {rightItems && rightItems.length > 0 && (
          <div className="flex w-full flex-col gap-y-6 lg:w-full lg:gap-y-12">
            {rightItems.map((spirit, index) => (
              <div key={index} className="flex flex-col items-start">
                {/* Render title and description */}
                <div className="mb-3 min-w-full rounded-2xl border-t-2 border-secondary-900 bg-primary-100 p-3 lg:mb-5 lg:min-w-[275px] lg:px-4 lg:py-5">
                  <span className="block text-center text-lg font-normal text-black-950 lg:text-h6 xl:text-h5">
                    {spirit.heading}
                  </span>
                </div>
                <p className="text-wrap text-base font-normal text-gray-500">
                  {spirit.description}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TeamSpiritSection;
